import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, CheckCircle2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const newMultipleChoice = () => ({ type: 'multiple_choice', question: '', options: ['', '', '', ''], correct_answer: 0 });
const newMultiSelect = () => ({ type: 'multi_select', question: '', options: ['', '', '', '', 'None of the above'], correct_answers: [] });
const newOrdering = () => ({ type: 'ordering', question: '', correct_order: ['Step 1', 'Step 2', 'Step 3'] });

export default function CreateTestPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [passingScore, setPassingScore] = useState(70);
  const [questions, setQuestions] = useState([newMultipleChoice()]);
  const [assignedTo, setAssignedTo] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api('get', '/users');
      setUsers(data.filter(u => u.role !== 'admin'));
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => { const q = [...prev]; q[idx] = { ...q[idx], [field]: value }; return q; });
  };

  const updateOption = (qIdx, optIdx, value) => {
    setQuestions(prev => {
      const q = [...prev]; const opts = [...q[qIdx].options]; opts[optIdx] = value;
      q[qIdx] = { ...q[qIdx], options: opts }; return q;
    });
  };

  const addQuestion = (type = 'multiple_choice') => {
    if (type === 'multi_select') setQuestions(prev => [...prev, newMultiSelect()]);
    else if (type === 'ordering') setQuestions(prev => [...prev, newOrdering()]);
    else setQuestions(prev => [...prev, newMultipleChoice()]);
  };

  const removeQuestion = (idx) => { if (questions.length <= 1) return; setQuestions(prev => prev.filter((_, i) => i !== idx)); };

  const addOption = (qIdx) => {
    setQuestions(prev => { const q = [...prev]; q[qIdx] = { ...q[qIdx], options: [...q[qIdx].options, ''] }; return q; });
  };

  const removeOption = (qIdx, optIdx) => {
    setQuestions(prev => {
      const q = [...prev]; const opts = q[qIdx].options.filter((_, i) => i !== optIdx);
      if (q[qIdx].type === 'multiple_choice') {
        let correct = q[qIdx].correct_answer;
        if (correct >= opts.length) correct = opts.length - 1;
        if (correct === optIdx) correct = 0;
        q[qIdx] = { ...q[qIdx], options: opts, correct_answer: correct };
      } else if (q[qIdx].type === 'multi_select') {
        const ca = (q[qIdx].correct_answers || []).filter(i => i !== optIdx).map(i => i > optIdx ? i - 1 : i);
        q[qIdx] = { ...q[qIdx], options: opts, correct_answers: ca };
      }
      return q;
    });
  };

  const toggleMultiSelectCorrect = (qIdx, optIdx) => {
    setQuestions(prev => {
      const q = [...prev]; const ca = [...(q[qIdx].correct_answers || [])];
      if (ca.includes(optIdx)) q[qIdx] = { ...q[qIdx], correct_answers: ca.filter(i => i !== optIdx) };
      else q[qIdx] = { ...q[qIdx], correct_answers: [...ca, optIdx] };
      return q;
    });
  };

  const updateOrderStep = (qIdx, stepIdx, value) => {
    setQuestions(prev => {
      const q = [...prev]; const steps = [...q[qIdx].correct_order]; steps[stepIdx] = value;
      q[qIdx] = { ...q[qIdx], correct_order: steps }; return q;
    });
  };

  const addOrderStep = (qIdx) => {
    setQuestions(prev => {
      const q = [...prev]; q[qIdx] = { ...q[qIdx], correct_order: [...q[qIdx].correct_order, `Step ${q[qIdx].correct_order.length + 1}`] }; return q;
    });
  };

  const removeOrderStep = (qIdx, stepIdx) => {
    setQuestions(prev => {
      const q = [...prev]; if (q[qIdx].correct_order.length <= 2) return q;
      q[qIdx] = { ...q[qIdx], correct_order: q[qIdx].correct_order.filter((_, i) => i !== stepIdx) }; return q;
    });
  };

  const moveOrderStep = (qIdx, stepIdx, dir) => {
    setQuestions(prev => {
      const q = [...prev]; const steps = [...q[qIdx].correct_order]; const newIdx = stepIdx + dir;
      if (newIdx < 0 || newIdx >= steps.length) return q;
      [steps[stepIdx], steps[newIdx]] = [steps[newIdx], steps[stepIdx]];
      q[qIdx] = { ...q[qIdx], correct_order: steps }; return q;
    });
  };

  const toggleAssign = (uid) => {
    setAssignedTo(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Test title is required'); return; }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) { toast.error(`Question ${i + 1} text is required`); return; }
      if (q.type === 'ordering') {
        const validSteps = q.correct_order.filter(s => s.trim());
        if (validSteps.length < 2) { toast.error(`Question ${i + 1} needs at least 2 steps`); return; }
      } else {
        const validOpts = q.options.filter(o => o.trim());
        if (validOpts.length < 2) { toast.error(`Question ${i + 1} needs at least 2 options`); return; }
        if (q.type === 'multi_select' && (!q.correct_answers || q.correct_answers.length === 0)) {
          toast.error(`Question ${i + 1} needs at least one correct answer selected`); return;
        }
      }
    }
    setSaving(true);
    try {
      const cleanQuestions = questions.map(q => {
        if (q.type === 'ordering') {
          return { type: 'ordering', question: q.question.trim(), correct_order: q.correct_order.filter(s => s.trim()) };
        } else if (q.type === 'multi_select') {
          return { type: 'multi_select', question: q.question.trim(), options: q.options.filter(o => o.trim()), correct_answers: q.correct_answers };
        }
        return { type: 'multiple_choice', question: q.question.trim(), options: q.options.filter(o => o.trim()), correct_answer: q.correct_answer };
      });
      const { data } = await api('post', '/tests/create', { title: title.trim(), questions: cleanQuestions, passing_score: passingScore, assigned_to: assignedTo });
      toast.success(`Test created with ${data.questions_count} question${data.questions_count !== 1 ? 's' : ''}${data.assigned_count > 0 ? `, assigned to ${data.assigned_count} user${data.assigned_count !== 1 ? 's' : ''}` : ''}`);
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally { setSaving(false); }
  };

  const getTypeBadge = (type) => {
    if (type === 'multi_select') return <Badge className="bg-violet-100 text-violet-700 text-[10px]">Select All</Badge>;
    if (type === 'ordering') return <Badge className="bg-teal-100 text-teal-700 text-[10px]">Ordering</Badge>;
    return <Badge className="bg-sky-100 text-sky-700 text-[10px]">Choice</Badge>;
  };

  return (
    <div className="pb-20 md:pb-6 max-w-3xl mx-auto">
      <motion.div {...fadeUp} className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-stone-600 hover:text-stone-900 -ml-2 rounded-xl" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="save-test-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Creating...' : 'Create Test'}
          </Button>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900 mt-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Create Custom Test
        </h1>
      </motion.div>

      <motion.div {...fadeUp} transition={{ delay: 0.05 }}>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="test-info-section">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-stone-700 font-medium text-sm">Test Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-xl border-stone-200 mt-1 text-lg font-semibold"
                placeholder="e.g. Food Safety Certification" data-testid="test-title-input" style={{ fontFamily: "'Outfit', sans-serif" }} />
            </div>
            <div className="max-w-[200px]">
              <Label className="text-stone-700 font-medium text-sm">Passing Score (%)</Label>
              <Input type="number" min={0} max={100} value={passingScore} onChange={e => setPassingScore(Number(e.target.value))}
                className="rounded-xl border-stone-200 mt-1" data-testid="passing-score-input" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Questions */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Questions ({questions.length})</h2>
        <div className="flex gap-2">
          <Select onValueChange={(v) => addQuestion(v)}>
            <SelectTrigger className="rounded-xl text-sm border-stone-200 w-auto" data-testid="add-question-type">
              <Plus className="w-4 h-4 mr-1" /> <span>Add Question</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
              <SelectItem value="multi_select">Select All That Apply</SelectItem>
              <SelectItem value="ordering">Ordering (Drag & Drop)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3 mb-6" data-testid="questions-list">
        {questions.map((q, qIdx) => (
          <motion.div key={qIdx} {...fadeUp} transition={{ delay: qIdx * 0.03 }}>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`question-editor-${qIdx}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A] shrink-0">Q{qIdx + 1}</Badge>
                    {getTypeBadge(q.type)}
                  </div>
                  {questions.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeQuestion(qIdx)} className="text-red-400 hover:text-red-600 p-1 h-7 w-7" data-testid={`remove-question-${qIdx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  <Input value={q.question} onChange={e => updateQuestion(qIdx, 'question', e.target.value)}
                    className="rounded-xl border-stone-200 font-medium" placeholder={q.type === 'ordering' ? 'Put these steps in the correct order:' : 'Enter your question...'} data-testid={`question-text-${qIdx}`} />

                  {/* Multiple Choice Editor */}
                  {q.type === 'multiple_choice' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-stone-500">Options (click radio to set correct answer)</Label>
                      {q.options.map((opt, optIdx) => (
                        <div key={optIdx} className="flex items-center gap-2">
                          <button type="button" onClick={() => updateQuestion(qIdx, 'correct_answer', optIdx)}
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${q.correct_answer === optIdx ? 'border-green-500 bg-green-500' : 'border-stone-300 hover:border-stone-400'}`}
                            data-testid={`correct-option-${qIdx}-${optIdx}`}>
                            {q.correct_answer === optIdx && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </button>
                          <Input value={opt} onChange={e => updateOption(qIdx, optIdx, e.target.value)}
                            className={`rounded-lg border-stone-200 text-sm flex-1 ${q.correct_answer === optIdx ? 'border-green-300 bg-green-50' : ''}`}
                            placeholder={`Option ${optIdx + 1}`} data-testid={`option-input-${qIdx}-${optIdx}`} />
                          {q.options.length > 2 && (
                            <Button variant="ghost" size="sm" onClick={() => removeOption(qIdx, optIdx)} className="p-1 h-7 w-7 text-stone-400 hover:text-red-500" data-testid={`remove-option-${qIdx}-${optIdx}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addOption(qIdx)} className="text-xs text-[#1B2A4A] h-7" data-testid={`add-option-${qIdx}`}>
                        <Plus className="w-3 h-3 mr-0.5" /> Add Option
                      </Button>
                    </div>
                  )}

                  {/* Multi-Select Editor */}
                  {q.type === 'multi_select' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-stone-500">Options (click checkboxes to mark correct answers)</Label>
                      {q.options.map((opt, optIdx) => (
                        <div key={optIdx} className="flex items-center gap-2">
                          <button type="button" onClick={() => toggleMultiSelectCorrect(qIdx, optIdx)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${(q.correct_answers || []).includes(optIdx) ? 'border-green-500 bg-green-500' : 'border-stone-300 hover:border-stone-400'}`}
                            data-testid={`correct-multi-${qIdx}-${optIdx}`}>
                            {(q.correct_answers || []).includes(optIdx) && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </button>
                          <Input value={opt} onChange={e => updateOption(qIdx, optIdx, e.target.value)}
                            className={`rounded-lg border-stone-200 text-sm flex-1 ${(q.correct_answers || []).includes(optIdx) ? 'border-green-300 bg-green-50' : ''}`}
                            placeholder={optIdx === q.options.length - 1 && opt.toLowerCase().includes('none') ? 'None of the above' : `Option ${optIdx + 1}`}
                            data-testid={`option-input-${qIdx}-${optIdx}`} />
                          {q.options.length > 2 && (
                            <Button variant="ghost" size="sm" onClick={() => removeOption(qIdx, optIdx)} className="p-1 h-7 w-7 text-stone-400 hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addOption(qIdx)} className="text-xs text-[#1B2A4A] h-7">
                        <Plus className="w-3 h-3 mr-0.5" /> Add Option
                      </Button>
                    </div>
                  )}

                  {/* Ordering Editor */}
                  {q.type === 'ordering' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-stone-500">Steps in correct order (top to bottom)</Label>
                      {q.correct_order.map((step, sIdx) => (
                        <div key={sIdx} className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-stone-300 shrink-0" />
                          <span className="text-xs text-stone-400 w-5 shrink-0">{sIdx + 1}.</span>
                          <Input value={step} onChange={e => updateOrderStep(qIdx, sIdx, e.target.value)}
                            className="rounded-lg border-stone-200 text-sm flex-1" placeholder={`Step ${sIdx + 1}`}
                            data-testid={`order-step-${qIdx}-${sIdx}`} />
                          <Button variant="ghost" size="sm" onClick={() => moveOrderStep(qIdx, sIdx, -1)} disabled={sIdx === 0}
                            className="p-1 h-7 w-7 text-stone-400"><ArrowUp className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => moveOrderStep(qIdx, sIdx, 1)} disabled={sIdx === q.correct_order.length - 1}
                            className="p-1 h-7 w-7 text-stone-400"><ArrowDown className="w-3 h-3" /></Button>
                          {q.correct_order.length > 2 && (
                            <Button variant="ghost" size="sm" onClick={() => removeOrderStep(qIdx, sIdx)} className="p-1 h-7 w-7 text-stone-400 hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => addOrderStep(qIdx)} className="text-xs text-[#1B2A4A] h-7" data-testid={`add-step-${qIdx}`}>
                        <Plus className="w-3 h-3 mr-0.5" /> Add Step
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Assign to Users */}
      <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="assign-section">
          <CardContent className="p-5">
            <h2 className="text-base font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Assign to Users <span className="text-stone-400 font-normal text-sm">(optional)</span>
            </h2>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {users.map(u => (
                <label key={u.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                  <input type="checkbox" checked={assignedTo.includes(u.id)} onChange={() => toggleAssign(u.id)} className="rounded accent-[#1B2A4A]" />
                  <span className="text-sm text-stone-900 flex-1">{u.name}</span>
                  {u.department && <Badge className={u.department === 'FOH' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-orange-100 text-orange-700 text-xs'}>{u.department}</Badge>}
                  <Badge className="bg-stone-100 text-stone-600 text-xs">{u.role}</Badge>
                </label>
              ))}
            </div>
            {assignedTo.length > 0 && <p className="text-xs text-stone-500 mt-2">{assignedTo.length} user{assignedTo.length !== 1 ? 's' : ''} will be notified</p>}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
